import { useState, useEffect } from 'react';

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  const slides = [
    {
      id: 1,
      title: 'Season 18',
      subtitle: 'Experience the Magic of Dance',
      description: 'Join us for our spectacular 18th season featuring world-class performances and talented dancers.',
      image: 'img/02032025_PNB_0832022.webp',
      cta: 'Learn More',
      ctaSecondary: 'View Schedule'
    },
    {
      id: 2,
      title: 'Enroll in Academy',
      subtitle: 'Nurture Your Passion for Dance',
      description: 'Comprehensive dance education for all ages and skill levels with world-class instruction.',
      image: 'img/1_Rp1BWave_037.webp',
      cta: 'Enroll Now',
      ctaSecondary: 'Schedule Tour'
    },
    {
      id: 3,
      title: 'Theater Events',
      subtitle: 'Unforgettable Performances',
      description: 'Experience breathtaking ballet performances that will inspire and captivate audiences of all ages.',
      image: 'img/1329000_N21.jpg',
      cta: 'Get Tickets',
      ctaSecondary: 'View Gallery'
    }
  ];

  useEffect(() => {
    setIsLoaded(true);
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="relative h-[700px] overflow-hidden">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-all duration-1000 ${
            index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
          }`}
        >
          <div
            className="hero-slide"
            style={{
              backgroundImage: `url(${slide.image})`
            }}
          >
            <div className="container-custom text-center text-white relative z-10">
              <div className="max-w-4xl mx-auto">
                <div className={`transition-all duration-1000 delay-300 ${
                  index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}>
                  <h2 className="text-6xl md:text-7xl lg:text-8xl font-elegant font-bold mb-6 leading-tight">
                    {slide.title}
                  </h2>
                  <h3 className="text-3xl md:text-4xl font-light mb-8 text-ballet-gold">
                    {slide.subtitle}
                  </h3>
                  <p className="text-xl md:text-2xl mb-12 leading-relaxed max-w-3xl mx-auto">
                    {slide.description}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <button className="btn-primary text-lg px-10 py-5 shadow-glow">
                      {slide.cta}
                    </button>
                    <button className="btn-outline text-lg px-10 py-5 bg-white/10 border-white text-white hover:bg-white hover:text-ballet-burgundy">
                      {slide.ctaSecondary}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute top-20 left-10 w-4 h-4 bg-ballet-gold rounded-full animate-float opacity-60"></div>
            <div className="absolute top-40 right-20 w-6 h-6 bg-ballet-pink rounded-full animate-bounce-gentle opacity-40"></div>
            <div className="absolute bottom-32 left-20 w-3 h-3 bg-white rounded-full animate-float opacity-50"></div>
          </div>
        </div>
      ))}

      {/* Enhanced Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-6 top-1/2 transform -translate-y-1/2 glass text-white p-4 rounded-2xl transition-all duration-300 hover:scale-110 hover:shadow-glow group"
        aria-label="Previous slide"
      >
        <svg className="w-6 h-6 group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-6 top-1/2 transform -translate-y-1/2 glass text-white p-4 rounded-2xl transition-all duration-300 hover:scale-110 hover:shadow-glow group"
        aria-label="Next slide"
      >
        <svg className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Enhanced Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-4">
        {slides.map((slide, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`group transition-all duration-300 ${
              index === currentSlide ? 'scale-110' : 'hover:scale-105'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          >
            <div className={`w-4 h-4 rounded-full transition-all duration-300 ${
              index === currentSlide
                ? 'bg-ballet-gold shadow-glow'
                : 'bg-white/50 group-hover:bg-white/70'
            }`} />
            <div className={`mt-2 text-xs text-white/80 transition-all duration-300 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'
            }`}>
              {slide.title}
            </div>
          </button>
        ))}
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
        <div
          className="h-full bg-ballet-gold transition-all duration-300"
          style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
        />
      </div>
    </div>
  );
};

export default HeroSlider;
